import React from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePublicInsightDetails } from '../hooks/usePublicInsightDetails';
import { useValidateShareCode } from '../hooks/useValidateShareCode';
import InsightDetailsHeader from './InsightDetailsHeader';
import InsightDetailsContent from './InsightDetailsContent';
import InsightDetailsSkeleton from './InsightDetailsSkeleton';
import ErrorScreen from '../../../core-components/theme/components/ErrorScreen';
import { StateArea } from '../../../core-components/theme/components/Screen';
import StatusMessageScreen from '../../../core-components/theme/components/StatusMessageScreen';
import { useServerSession } from '../../../session';
import 'react-quill/dist/quill.snow.css';

const PublicInsightDetails: React.FC = () => {
  const { t } = useTranslation();
  const { insightId = '', shareCode = '' } = useParams<{ insightId: string; shareCode?: string }>();
  const session = useServerSession();
  const sessionKnown = session.status !== 'pending';
  const isLoggedIn = session.status === 'signedIn';

  const shouldValidate = sessionKnown && !isLoggedIn && !!shareCode;
  const { isAccessible, isValidating, validationError, message } = useValidateShareCode(shareCode, shouldValidate);

  const shouldFetchDetails = isLoggedIn || (shouldValidate && isAccessible === true);
  const { insightDetails, isInsightDetailsLoading, insightDetailsError } = usePublicInsightDetails(
    insightId,
    shouldFetchDetails && !!insightId
  );

  if (!insightId) {
    return (
      <StateArea>
        <ErrorScreen />
      </StateArea>
    );
  }

  if (!sessionKnown || (shouldValidate && isValidating)) {
    return <InsightDetailsSkeleton />;
  }

  if (shouldValidate && !isValidating && (validationError || isAccessible === false)) {
    return (
      <StateArea>
        <StatusMessageScreen
          type="negative"
          messageHeading={t('insights.public.accessDenied', 'Access Denied')}
          messageBody={message || t('insights.public.linkExpired', 'This link is no longer accessible or has expired.')}
        />
      </StateArea>
    );
  }

  if (isInsightDetailsLoading) {
    return <InsightDetailsSkeleton />;
  }

  if (insightDetailsError) {
    return (
      <StateArea>
        <ErrorScreen />
      </StateArea>
    );
  }

  if (!insightDetails) {
    return (
      <StateArea>
        <ErrorScreen />
      </StateArea>
    );
  }

  return (
    <Box>
      <InsightDetailsHeader insightDetails={insightDetails} isPublic={true} />
      <InsightDetailsContent insightId={insightId} insightDetails={insightDetails} isPublic={true} />
    </Box>
  );
};

export default PublicInsightDetails;

